import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DailyIframe from '@daily-co/daily-js';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const SessionRoom = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { socket } = useSocket();
  const { user } = useAuth();

  // Session data from navigation or API
  const [sessionData, setSessionData] = useState(location.state?.sessionData || null);
  const [partnerInfo] = useState(location.state?.partnerInfo || null);
  const [loading, setLoading] = useState(!sessionData);

  // Daily.co call object
  const [callObject, setCallObject] = useState(null);
  const [participants, setParticipants] = useState({});  // eslint-disable-line no-unused-vars
  // const [localVideoTrack, setLocalVideoTrack] = useState(null);
  // const [remoteVideoTracks, setRemoteVideoTracks] = useState([]);

  // Session state
  const [sessionStatus, setSessionStatus] = useState('waiting'); // waiting, active, ended
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [timeUntilStart, setTimeUntilStart] = useState(null); // Countdown until session starts
  const [sessionStartTime, setSessionStartTime] = useState(null);  // eslint-disable-line no-unused-vars
  const [partnerJoined, setPartnerJoined] = useState(false);

  // Audio/Video controls
  const [isMicOn, setIsMicOn] = useState(true);  // eslint-disable-line no-unused-vars
  const [isCameraOn, setIsCameraOn] = useState(true);  // eslint-disable-line no-unused-vars
  const [isScreenSharing, setIsScreenSharing] = useState(false);  // eslint-disable-line no-unused-vars

  // Chat
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');  // eslint-disable-line no-unused-vars
  // const [isChatOpen, setIsChatOpen] = useState(false);
  const chatEndRef = useRef(null);

  // Task description modal
  const [showTaskModal, setShowTaskModal] = useState(false);

  // Check-in notifications
  const [checkInPoints, setCheckInPoints] = useState([]);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [currentCheckIn, setCurrentCheckIn] = useState(null);

  // Video container ref
  const videoContainerRef = useRef(null);

  // Timer interval
  const timerIntervalRef = useRef(null);

  // Fetch session data if not passed via navigation
  useEffect(() => {
    const fetchSessionData = async () => {
      if (!sessionData) {
        try {
          const response = await api.get(`/sessions/${sessionId}`);
          setSessionData(response.data);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching session:', error);
          alert('Failed to load session. Redirecting to dashboard.');
          navigate('/dashboard');
        }
      }
    };

    fetchSessionData();
  }, [sessionId, sessionData, navigate]);

  // Create Daily.co room if it doesn't exist
  useEffect(() => {
    const createRoomIfNeeded = async () => {
      if (!sessionData?.session) return;

      // If room_url doesn't exist, create the room
      if (!sessionData.session.room_url) {
        console.log('Room URL not found, creating Daily.co room...');
        try {
          const response = await api.post('/sessions/create', { sessionId });
          console.log('Daily.co room created:', response.data);

          // Update session data with room URL
          setSessionData(prev => ({
            ...prev,
            session: {
              ...prev.session,
              room_url: response.data.roomUrl,
              room_name: response.data.roomName,
            }
          }));
        } catch (error) {
          console.error('Error creating Daily.co room:', error);
          alert('Failed to create video room. Please try again.');
        }
      }
    };

    createRoomIfNeeded();
  }, [sessionData, sessionId]);

  // Join session room via WebSocket
  useEffect(() => {
    if (!socket || !user) return;

    socket.emit('enter-session-room', {
      sessionId,
      userId: user.id,
    });

    // Listen for partner entering
    socket.on('partner-entered-room', (data) => {
      setPartnerJoined(true);
    });

    // Listen for check-in added
    socket.on('checkin-added', (data) => {
      setCheckInPoints(data.checkInPoints);
    });

    // Listen for session ended
    socket.on('session-ended', () => {
      handleSessionEnd();
    });

    // Listen for chat messages
    socket.on('chat-message', (data) => {
      setMessages((prev) => [...prev, data]);
    });

    return () => {
      socket.off('partner-entered-room');
      socket.off('checkin-added');
      socket.off('session-ended');
      socket.off('chat-message');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user, sessionId, sessionData]);

  // Initialize timer based on scheduled start time
  useEffect(() => {
    if (!sessionData?.session) return;

    const scheduledStartTime = new Date(sessionData.session.start_time);
    const durationMinutes = sessionData.session.duration_minutes;
    const now = new Date();

    console.log('Timer initialization:', {
      scheduledStartTime: scheduledStartTime.toISOString(),
      now: now.toISOString(),
      durationMinutes,
      nowBeforeStart: now < scheduledStartTime,
    });

    // Calculate time remaining based on scheduled start and end times
    const scheduledEndTime = new Date(scheduledStartTime.getTime() + durationMinutes * 60 * 1000);

    console.log('Session timing check:', {
      now: now.toISOString(),
      scheduledStartTime: scheduledStartTime.toISOString(),
      scheduledEndTime: scheduledEndTime.toISOString(),
      nowBeforeStart: now < scheduledStartTime,
      nowBeforeEnd: now < scheduledEndTime,
      rawStartTimeFromBackend: sessionData.session.start_time,
      timeDifferenceMinutes: Math.round((scheduledStartTime - now) / 1000 / 60),
    });

    if (now < scheduledStartTime) {
      // Session hasn't started yet - wait until start time
      const millisUntilStart = scheduledStartTime - now;
      const secondsUntilStart = Math.floor(millisUntilStart / 1000);
      console.log(`Session starts in ${secondsUntilStart} seconds`);
      setSessionStatus('waiting');
      setTimeUntilStart(secondsUntilStart);

      const startTimer = setTimeout(() => {
        console.log('Timer starting now!');
        setSessionStatus('active');
        setSessionStartTime(scheduledStartTime);
        setTimeRemaining(durationMinutes * 60);
        setTimeUntilStart(null);
      }, millisUntilStart);

      return () => clearTimeout(startTimer);
    } else if (now < scheduledEndTime) {
      // Session is currently active
      const secondsRemaining = Math.floor((scheduledEndTime - now) / 1000);
      console.log(`Session active with ${secondsRemaining} seconds remaining`);
      setSessionStatus('active');
      setSessionStartTime(scheduledStartTime);
      setTimeRemaining(secondsRemaining);
    } else {
      // Session has already ended - but don't immediately exit!
      // Let the user see they missed it
      console.log('Session already ended - allowing user to see the room');
      setSessionStatus('ended');
      setTimeRemaining(0);

      // Show a message instead of immediately exiting
      setTimeout(() => {
        alert('This session has already ended.');
        navigate('/dashboard');
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData]);

  // Initialize Daily.co video call
  useEffect(() => {
    if (!sessionData?.session?.room_url || callObject) return;

    let daily = null;
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 10;

    const initializeCall = async () => {
      try {
        // Wait for container to be available
        while (!videoContainerRef.current && retryCount < maxRetries) {
          console.log('Waiting for video container...', retryCount);
          await new Promise(resolve => setTimeout(resolve, 100));
          retryCount++;
        }

        if (!videoContainerRef.current) {
          console.error('Video container ref not available after retries');
          return;
        }

        // Destroy any existing Daily instance first
        const existingCallInstance = DailyIframe.getCallInstance();
        if (existingCallInstance) {
          console.log('Destroying existing Daily instance');
          await existingCallInstance.destroy();
        }

        // Check if an iframe already exists and remove it
        const existingIframe = videoContainerRef.current.querySelector('iframe');
        if (existingIframe) {
          console.log('Removing existing iframe');
          existingIframe.remove();
        }

        console.log('Initializing Daily.co with room URL:', sessionData.session.room_url);

        // Create Daily Prebuilt UI with chat enabled
        daily = DailyIframe.createFrame(videoContainerRef.current, {
          iframeStyle: {
            position: 'absolute',
            width: '100%',
            height: '100%',
            border: '0',
            borderRadius: '0.5rem',
          },
          showLeaveButton: true,
          showFullscreenButton: true,
          showLocalVideo: true,
          showParticipantsBar: true,
          theme: {
            colors: {
              accent: '#3B82F6',
              accentText: '#FFFFFF',
              background: '#1F2937',
              backgroundAccent: '#374151',
              baseText: '#FFFFFF',
              border: '#4B5563',
              mainAreaBg: '#111827',
              mainAreaBgAccent: '#1F2937',
              mainAreaText: '#FFFFFF',
              supportiveText: '#9CA3AF',
            },
          },
        });

        if (!mounted) {
          daily.destroy();
          return;
        }

        // Handle Daily.co events before joining
        daily
          .on('joined-meeting', (event) => {
            console.log('Successfully joined Daily.co meeting');
            if (mounted) {
              setParticipants(daily.participants());
            }
          })
          .on('participant-joined', (event) => {
            console.log('Participant joined:', event.participant);
            if (mounted) {
              setPartnerJoined(true);
              setParticipants(daily.participants());
            }
          })
          .on('participant-left', (event) => {
            console.log('Participant left:', event.participant);
            if (mounted) {
              setPartnerJoined(false);
              setParticipants(daily.participants());
            }
          })
          .on('participant-updated', (event) => {
            if (mounted) {
              setParticipants(daily.participants());
            }
          })
          .on('track-started', (event) => {
            console.log('Track started:', event);
          })
          .on('error', (error) => {
            console.error('Daily.co error:', error);
            if (mounted) {
              alert(`Video call error: ${error.errorMsg || 'Unknown error'}`);
            }
          })
          .on('left-meeting', (event) => {
            console.log('Left Daily.co meeting', event);
            console.log('Mounted status:', mounted);
            console.log('Calling handleSessionEnd...');
            handleSessionEnd().catch(err => {
              console.error('Error in handleSessionEnd:', err);
              navigate('/dashboard');
            });
          })
          .on('leaving-meeting', (event) => {
            console.log('Leaving Daily.co meeting', event);
          });

        setCallObject(daily);

        // Join the Daily.co room
        await daily.join({ url: sessionData.session.room_url });

        console.log('Successfully joined Daily.co room');

      } catch (error) {
        console.error('Error initializing Daily.co:', error);
        if (mounted) {
          if (error.errorMsg) {
            alert(`Failed to connect to video call: ${error.errorMsg}`);
          } else {
            alert('Failed to connect to video call.');
          }
        }
      }
    };

    initializeCall();

    return () => {
      mounted = false;
      if (daily) {
        console.log('Cleaning up Daily instance');
        // Use a timeout to ensure cleanup happens after any pending operations
        setTimeout(() => {
          daily.destroy().catch(err => {
            // Silently catch errors during cleanup
            console.log('Cleanup: Daily instance already destroyed');
          });
        }, 0);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData]);

  // Countdown until session starts
  useEffect(() => {
    if (sessionStatus === 'waiting' && timeUntilStart !== null && timeUntilStart > 0) {
      const countdownInterval = setInterval(() => {
        setTimeUntilStart((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [sessionStatus, timeUntilStart]);

  // Timer countdown for active session
  useEffect(() => {
    if (sessionStatus === 'active' && timeRemaining !== null) {
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            handleSessionEnd();
            return 0;
          }

          // Check for check-in notifications
          const minutesRemaining = Math.ceil(prev / 60);
          if (checkInPoints.includes(minutesRemaining) && !currentCheckIn) {
            triggerCheckInNotification(minutesRemaining);
          }

          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionStatus, timeRemaining, checkInPoints, currentCheckIn]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Trigger check-in notification
  const triggerCheckInNotification = (minutes) => {
    setCurrentCheckIn(minutes);
    setShowCheckInModal(true);

    // Play notification sound (optional)
    const audio = new Audio('/notification.mp3');
    audio.play().catch(e => console.log('Could not play sound:', e));

    // Auto-hide after 5 seconds
    setTimeout(() => {
      setShowCheckInModal(false);
      setCurrentCheckIn(null);
    }, 5000);
  };

  // Toggle microphone
  // const toggleMic = useCallback(() => {
  //   if (callObject) {
  //     callObject.setLocalAudio(!isMicOn);
  //     setIsMicOn(!isMicOn);
  //   }
  // }, [callObject, isMicOn]);

  // Toggle camera
  // const toggleCamera = useCallback(() => {
  //   if (callObject) {
  //     callObject.setLocalVideo(!isCameraOn);
  //     setIsCameraOn(!isCameraOn);
  //   }
  // }, [callObject, isCameraOn]);

  // Toggle screen sharing
  // const toggleScreenShare = useCallback(async () => {
  //   if (callObject) {
  //     try {
  //       if (isScreenSharing) {
  //         await callObject.stopScreenShare();
  //         setIsScreenSharing(false);
  //       } else {
  //         await callObject.startScreenShare();
  //         setIsScreenSharing(true);
  //       }
  //     } catch (error) {
  //       console.error('Error toggling screen share:', error);
  //       alert('Failed to toggle screen sharing.');
  //     }
  //   }
  // }, [callObject, isScreenSharing]);

  // Send chat message
  // const sendMessage = () => {
  //   if (!newMessage.trim() || !socket) return;

  //   const messageData = {
  //     sessionId,
  //     userId: user.id,
  //     userName: user.first_name,
  //     message: newMessage,
  //     timestamp: new Date().toISOString(),
  //   };

  //   socket.emit('send-chat-message', messageData);
  //   setMessages((prev) => [...prev, messageData]);
  //   setNewMessage('');
  // };

  // Add check-in point
  // const addCheckInPoint = () => {
  //   if (!socket || timeRemaining === null) return;

  //   const minutesRemaining = Math.ceil(timeRemaining / 60);

  //   socket.emit('add-checkin-point', {
  //     sessionId,
  //     minutes: minutesRemaining,
  //   });
  // };

  // End session
  const handleSessionEnd = async () => {
    console.log('handleSessionEnd called, current status:', sessionStatus);
    if (sessionStatus === 'ended') {
      console.log('Session already ended, skipping');
      return;
    }

    setSessionStatus('ended');
    console.log('Set session status to ended');

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    try {
      const actualDuration = sessionData
        ? sessionData.session.duration_minutes - Math.ceil(timeRemaining / 60)
        : 0;

      console.log('Ending session via API with duration:', actualDuration);

      // End session via API
      await api.put(`/sessions/${sessionId}/end`, {
        actualDuration,
      });

      console.log('Session ended via API');

      // Notify via WebSocket
      if (socket) {
        socket.emit('end-session', {
          sessionId,
          actualDuration,
        });
        console.log('Notified via WebSocket');
      }

      // Leave Daily.co call if not already left
      if (callObject) {
        try {
          const meetingState = callObject.meetingState();
          console.log('Meeting state:', meetingState);
          if (meetingState !== 'left-meeting') {
            await callObject.leave();
          }
          callObject.destroy();
          console.log('Call object cleaned up');
        } catch (err) {
          console.log('Error cleaning up call object:', err);
        }
      }

      // Redirect to dashboard immediately
      console.log('Navigating to dashboard...');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error ending session:', error);
      // Still redirect even if there's an error
      console.log('Navigating to dashboard despite error...');
      navigate('/dashboard');
    }
  };

  // Format time (seconds to MM:SS)
  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">Loading session...</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
        {/* Header */}
        <div className="bg-gray-800 px-6 py-4 flex items-center justify-between border-b border-gray-700">
        <div>
          <h1 className="text-xl font-semibold text-white">
            Focus Session
            {/* Show User Name */}
            {user && (
              <span className="ml-3 text-sm bg-purple-600 text-white px-3 py-1 rounded-full">
                {user.first_name || user.email}
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-400">
            {sessionStatus === 'waiting' && timeUntilStart !== null && 'Session starting soon...'}
            {sessionStatus === 'waiting' && timeUntilStart === null && 'Waiting to start...'}
            {sessionStatus === 'active' && 'Session in progress'}
            {sessionStatus === 'ended' && 'Session ended'}
          </p>
        </div>

        {/* Timer */}
        <div className="text-center">
          {sessionStatus === 'waiting' && timeUntilStart !== null ? (
            <>
              <div className="text-4xl font-bold text-yellow-400 font-mono">
                {formatTime(timeUntilStart)}
              </div>
              <div className="text-sm text-gray-400 mt-1">
                Session starts in...
              </div>
            </>
          ) : (
            <>
              <div className="text-4xl font-bold text-white font-mono">
                {formatTime(timeRemaining)}
              </div>
              <div className="text-sm text-gray-400 mt-1">
                {sessionData?.session?.duration_minutes} min session
              </div>
            </>
          )}
        </div>

        {/* Partner status */}
        <div className="text-right">
          <button
            onClick={() => setShowTaskModal(true)}
            className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer underline decoration-dotted"
          >
            {partnerInfo?.name || sessionData?.partner?.first_name || 'Partner'}'s Info
          </button>
          <div className="flex items-center justify-end gap-2 mt-1">
            <div
              className={`w-2 h-2 rounded-full ${
                partnerJoined ? 'bg-green-500' : 'bg-gray-500'
              }`}
            />
            <span className="text-xs text-gray-400">
              {partnerJoined ? 'Connected' : 'Not connected'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-auto">
        {/* Video Area */}
        <div className="flex-1 flex flex-col p-4 gap-4">
          {/* Daily.co video container */}
          <div className="flex-1 bg-gray-900 rounded-lg relative min-h-0">
            <div
              ref={videoContainerRef}
              className="w-full h-full relative"
              style={{ minHeight: '400px' }}
            >
              {!callObject && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
                  <div className="text-center">
                    <div className="text-gray-500 text-xl mb-2">Connecting to video room...</div>
                    <div className="text-gray-600 text-sm">
                      {sessionStatus === 'waiting' ? 'Waiting for session to start' : 'Initializing video call'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Partner Task Modal */}
      {showTaskModal && (sessionData?.partner || partnerInfo) && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="mb-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">
                {partnerInfo?.name || sessionData?.partner?.first_name}'s Task
              </h3>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="px-2 py-1 bg-gray-100 rounded-md">
                  {(partnerInfo?.category || sessionData?.partner?.category)?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'General'}
                </span>
                {(partnerInfo?.location || sessionData?.partner?.location) && (
                  <span className="text-gray-400">
                    📍 {partnerInfo?.location || sessionData?.partner?.location}
                  </span>
                )}
              </div>
            </div>
            <div className="mb-4">
              <p className="text-gray-700 whitespace-pre-wrap">
                {partnerInfo?.description || sessionData?.partner?.description || 'No description provided'}
              </p>
            </div>

            {/* Session Preferences */}
            {partnerInfo?.sessionPreferences && (
              <div className="mb-6 border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Session Type Preferences</h4>
                <div className="space-y-2">
                  {partnerInfo.sessionPreferences.acceptSocial && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-green-600">✓</span>
                      <span>Open to <strong>Social Sessions</strong> (chatting during work time)</span>
                    </div>
                  )}
                  {partnerInfo.sessionPreferences.acceptSilent && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-green-600">✓</span>
                      <span>Open to <strong>Silent Focus</strong> (no talking at all)</span>
                    </div>
                  )}
                  {partnerInfo.sessionPreferences.acceptFocusOnly && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-green-600">✓</span>
                      <span>Open to <strong>Focus Only</strong> (talk before/after only)</span>
                    </div>
                  )}
                </div>
                <p className="mt-3 text-xs text-gray-500 italic">
                  You and your partner can decide together which session style to use.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              {(partnerInfo?.id || sessionData?.partner?.id) && (
                <a
                  href={`/profile/${partnerInfo?.id || sessionData?.partner?.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-center"
                >
                  View Profile
                </a>
              )}
              <button
                onClick={() => setShowTaskModal(false)}
                className={`${(partnerInfo?.id || sessionData?.partner?.id) ? 'flex-1' : 'w-full'} px-6 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Check-in Notification Modal */}
      {showCheckInModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <div className="text-center">
              <div className="text-4xl mb-4">🔔</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Check-in Time!
              </h3>
              <p className="text-gray-600">
                {currentCheckIn} minutes remaining in your session
              </p>
              <button
                onClick={() => setShowCheckInModal(false)}
                className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionRoom;
